import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Button from '@/components/button'
import DefaultModal from '@/components/default-modal'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import type { OperadorResumo } from '@/types/operador'
import styles from './style.module.css'

const schema = yup.object({
  nome: yup.string().trim().required('Preencha o nome'),
  email: yup.string().trim().email('Informe um e-mail válido').required('Preencha o e-mail'),
  telefoneDDI: yup.string().trim().required('Preencha o DDI'),
  telefone: yup.string().trim().required('Preencha o telefone'),
})

export type OperadorFormValues = yup.InferType<typeof schema>

const valoresVazios: OperadorFormValues = {
  nome: '',
  email: '',
  telefoneDDI: '55',
  telefone: '',
}

interface OperadorFormModalProps {
  isOpen: boolean
  toggle: () => void
  operador: OperadorResumo | null
  isLoading?: boolean
  onSubmit: (valores: OperadorFormValues) => void
}

const OperadorFormModal = ({
  isOpen,
  isLoading = false,
  onSubmit,
  operador,
  toggle,
}: OperadorFormModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<OperadorFormValues>({
    resolver: yupResolver(schema),
    defaultValues: valoresVazios,
  })

  useEffect(() => {
    if (!isOpen) return

    reset(
      operador
        ? {
            nome: operador.nome ?? '',
            email: operador.email ?? '',
            telefoneDDI: operador.telefone1DDI ?? '55',
            telefone: operador.telefone1 ?? '',
          }
        : valoresVazios,
    )
  }, [isOpen, operador, reset])

  return (
    <DefaultModal
      isOpen={isOpen}
      toggle={toggle}
      title={operador ? 'Editar operador' : 'Novo operador'}
      isLoading={isLoading}
      footer={
        <>
          <Button variant="ghost" onClick={toggle}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="operador-form"
            isLoading={isLoading}
            disabled={Boolean(operador) && !isDirty}
          >
            {operador ? 'Salvar alterações' : 'Criar operador'}
          </Button>
        </>
      }
    >
      <form id="operador-form" className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField label="Nome" htmlFor="operador-nome" required error={errors.nome?.message}>
          <Input
            id="operador-nome"
            placeholder="Preencha o nome"
            invalid={Boolean(errors.nome)}
            {...register('nome')}
          />
        </FormField>

        <FormField label="Email" htmlFor="operador-email" required error={errors.email?.message}>
          <Input
            id="operador-email"
            type="email"
            placeholder="Preencha o e-mail"
            invalid={Boolean(errors.email)}
            {...register('email')}
          />
        </FormField>

        <div className={styles.linha}>
          <FormField label="DDI" htmlFor="operador-ddi" required error={errors.telefoneDDI?.message}>
            <Input
              id="operador-ddi"
              placeholder="Preencha o DDI"
              invalid={Boolean(errors.telefoneDDI)}
              {...register('telefoneDDI')}
            />
          </FormField>

          <FormField
            label="Telefone"
            htmlFor="operador-telefone"
            required
            error={errors.telefone?.message}
          >
            <Input
              id="operador-telefone"
              placeholder="Preencha o telefone"
              invalid={Boolean(errors.telefone)}
              {...register('telefone')}
            />
          </FormField>
        </div>
      </form>
    </DefaultModal>
  )
}

export default OperadorFormModal
